// ═══════════════════════════════════════════════════════════════════════════════
// FILE: src/app/services/cal-status.service.ts
// VERSION: v3.0.0 – Session/CAL Layer Separation
// DATE: 2026-05-03
//
// CHANGES v3.0.0:
//   ✅ FIX: 401 wird NICHT mehr als CAL-Problem interpretiert
//   ✅ FIX: Bei 401 → delegiert an SessionHealthService (kein eigener Dialog)
//   ✅ FIX: Periodic refresh stoppt sofort bei Session-Loss
//   ✅ FIX: Nur 402/410 werden als CAL-Fehler behandelt
//   ✅ FIX: Unknown errors setzen NICHT mehr blind 'no_license'
//   ✅ NEW: sessionExpired$ from SessionHealthService used to suppress CAL checks
//
// ARCHITECTURE:
//   Layer 1: SessionHealthService   → 401 handling (Session)
//   Layer 2: CalStatusService       → 402/410 handling (License)  ← THIS FILE
//   Layer 3: Guards / Routes        → Business Logic
//
//   RULE: Layer 2 darf NICHT feuern wenn Layer 1 kaputt ist.
// ═══════════════════════════════════════════════════════════════════════════════

import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, interval, Subscription } from 'rxjs';
import { tap, catchError, shareReplay } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import {
  CalErrorDialogComponent,
  CalErrorDialogData
} from '../shared/dialogs/cal-error-dialog/cal-error-dialog.component';
import { SessionHealthService } from './session-health.service';

// ═══════════════════════════════════════════════════════════════════════════════
// INTERFACES (unchanged)
// ═══════════════════════════════════════════════════════════════════════════════

export interface RedisMetaResponse {
  status: string;
  total: number;
  activeProjects: Array<{
    pslot: string;
    permission: number;
    project_id: number;
    project_name: string;
  }>;
  idleAccessRequests: Array<{
    pslot: string;
    permission: number;
    project_id: number;
    project_name: string;
  }>;
  orgAccessPermission: number;
  meta: {
    name: string;
    email: string;
    oid: string;
    oidc: string | null;
    password: string | null;
    salt: string | null;
    id_provider: string | null;
  };
  fingerprint: string;
  cal: {
    has_license: boolean;
    status: 'active' | 'suspended' | 'expired' | 'no_license';
    cal_id: number | null;
    expires_at: number | null;
    days_remaining: number | null;
    assigned_by: string | null;
    assigned_at: string | null;
    expiring_soon: boolean;
    expiring_critical: boolean;
  };
  tok?: {
    expires_at: number | null;
    issued_at: number | null;
    token_type: string;
    scope: string;
  };
}

export interface CalStatus {
  isValid: boolean;
  hasLicense: boolean;
  status: 'active' | 'suspended' | 'expired' | 'no_license';
  daysRemaining: number | null;
  expiresAt: number | null;
  expiringSoon: boolean;
  expiringCritical: boolean;
  lastChecked: Date;
}

export interface SilentCalCheckResult {
  calStatus: CalStatus;
  orgAccessPermission: number;
}

@Injectable({
  providedIn: 'root'
})
export class CalStatusService implements OnDestroy {

  private apiBaseUrl = this.constructApiBaseUrl();

  // Cached Redis response (5 minutes default)
  private cachedResponse$: Observable<RedisMetaResponse> | null = null;
  private cacheTimestamp = 0;
  private readonly CACHE_DURATION_MS = 5 * 60 * 1000;

  // CAL Status Stream
  private calStatusSubject = new BehaviorSubject<CalStatus | null>(null);
  public calStatus$ = this.calStatusSubject.asObservable();

  // Periodic refresh
  private refreshSubscription: Subscription | null = null;
  private readonly REFRESH_INTERVAL_MS = 10 * 60 * 1000;

  // Dialog management
  private dialogOpen = false;

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private sessionHealth: SessionHealthService   // ← NEU v3.0.0
  ) {
    console.log('[CalStatusService] v3.0.0 Initialized with API:', this.apiBaseUrl);
  }

  ngOnDestroy(): void {
    this.stopPeriodicRefresh();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Silent CAL check for Guard use (v2.0.0+):
   * - Returns CalStatus + orgAccessPermission
   * - Does NOT show any dialog
   * - v3.0.0: Correctly separates 401 (session) from CAL errors
   */
  async checkCalStatusSilent(): Promise<SilentCalCheckResult> {
    try {
      const response = await this.fetchRedisMetaCached().toPromise();

      if (!response) {
        throw new Error('No response from Redis meta endpoint');
      }

      const calStatus = this.parseCalStatus(response);
      this.calStatusSubject.next(calStatus);

      return {
        calStatus,
        orgAccessPermission: response.orgAccessPermission || 0
      };

    } catch (error: any) {
      // ─────────────────────────────────────────────────────────────────
      // v3.0.0 FIX: Session-Fehler (401) → an SessionHealthService delegieren
      // ─────────────────────────────────────────────────────────────────
      if (error instanceof HttpErrorResponse && error.status === 401) {
        console.warn('[CalStatusService] 🔐 401 detected in silent check → delegate to SessionHealth');
        this.stopPeriodicRefresh();
        this.sessionHealth.handleSessionExpired(error, 'cal-silent-check');

        // Throw weiter damit der Guard korrekt reagiert
        throw error;
      }

      console.error('[CalStatusService] Silent check failed:', error);

      // Nur bei echten CAL-Problemen (nicht 401!) den Status setzen
      const errorStatus = this.buildErrorCalStatus(error);
      this.calStatusSubject.next(errorStatus);

      return {
        calStatus: errorStatus,
        orgAccessPermission: 0
      };
    }
  }

  /**
   * Check CAL status (WITH dialog on failure)
   * Used for mid-session periodic refresh where dialog IS appropriate.
   * v3.0.0: 401 → SessionHealthService, KEIN CAL-Dialog
   */
  async checkCalStatus(): Promise<CalStatus | null> {
    // ── v3.0.0: Nicht prüfen wenn Session bereits als expired erkannt ──
    if (this.sessionHealth.isHandlingExpiry) {
      console.log('[CalStatusService] Session expiry in progress → skip CAL check');
      return this.calStatusSubject.value;
    }

    try {
      const response = await this.fetchRedisMetaCached().toPromise();

      if (!response) {
        throw new Error('No response from Redis meta endpoint');
      }

      const status = this.parseCalStatus(response);
      this.calStatusSubject.next(status);

      // Show dialog if license invalid (only for mid-session checks, NOT for 401!)
      if (!status.isValid) {
        this.showCalErrorDialog(response.cal);
      }

      return status;

    } catch (error: any) {
      // ─────────────────────────────────────────────────────────────────
      // v3.0.0 FIX: 401 = Session-Problem → KEIN CAL-Dialog!
      // ─────────────────────────────────────────────────────────────────
      if (error instanceof HttpErrorResponse && error.status === 401) {
        console.warn('[CalStatusService] 🔐 401 in periodic check → delegate to SessionHealth');
        this.stopPeriodicRefresh();
        this.sessionHealth.handleSessionExpired(error, 'cal-periodic-check');
        return null; // ← KEIN errorStatus setzen! Kein calStatusSubject.next()!
      }

      // ─────────────────────────────────────────────────────────────────
      // Echte CAL-Fehler (402, 410, Netzwerk, etc.)
      // ─────────────────────────────────────────────────────────────────
      console.error('[CalStatusService] Failed to check CAL status:', error);

      const errorStatus = this.buildErrorCalStatus(error);
      this.calStatusSubject.next(errorStatus);
      return errorStatus;
    }
  }

  /**
   * Start periodic CAL status refresh
   */
  startPeriodicRefresh(intervalMs: number = this.REFRESH_INTERVAL_MS): void {
    if (this.refreshSubscription) {
      return;
    }

    console.log(`[CalStatusService] Starting periodic refresh (every ${intervalMs / 60000} min)`);

    this.refreshSubscription = interval(intervalMs).subscribe(() => {
      console.log('[CalStatusService] Periodic refresh triggered');
      this.checkCalStatus();
    });
  }

  stopPeriodicRefresh(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
      this.refreshSubscription = null;
      console.log('[CalStatusService] Periodic refresh stopped');
    }
  }

  async forceRefresh(): Promise<CalStatus | null> {
    this.invalidateCache();
    return this.checkCalStatus();
  }

  getCurrentStatus(): CalStatus | null {
    return this.calStatusSubject.value;
  }

  getRedisMetaCached(): Observable<RedisMetaResponse> {
    return this.fetchRedisMetaCached();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIVATE METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  private fetchRedisMetaCached(): Observable<RedisMetaResponse> {
    const now = Date.now();
    const cacheAge = now - this.cacheTimestamp;

    if (this.cachedResponse$ && cacheAge < this.CACHE_DURATION_MS) {
      return this.cachedResponse$;
    }

    this.cachedResponse$ = this.http.get<RedisMetaResponse>(
      `${this.apiBaseUrl}/api/my_project_slots_from_redis`,
      { withCredentials: true }
    ).pipe(
      tap(response => {
        console.log('[CalStatusService] ✅ Redis meta loaded:', {
          hasLicense: response.cal?.has_license,
          status: response.cal?.status,
          orgPerm: response.orgAccessPermission,
          daysRemaining: response.cal?.days_remaining
        });
        this.cacheTimestamp = Date.now();
      }),
      catchError(error => {
        console.error('[CalStatusService] ❌ Failed to fetch Redis meta:', error);
        this.invalidateCache();

        // ── v3.0.0: Bei 401 sofort periodic refresh stoppen ──────────
        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.stopPeriodicRefresh();
        }

        throw error;
      }),
      shareReplay(1)
    );

    return this.cachedResponse$;
  }

  /**
   * v3.0.0 NEU: Baut einen CalStatus-Error-Wert, ABER differenziert nach Ursache.
   * Wird NICHT bei 401 aufgerufen!
   */
  private buildErrorCalStatus(error: any): CalStatus {
    let status: CalStatus['status'] = 'no_license';

    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 402:
          status = 'no_license';
          break;
        case 410:
          status = 'expired';
          break;
        default:
          // Netzwerk-Fehler, 500er etc. → konservativ 'no_license'
          // aber NICHT bei 401 (der wird oben abgefangen)
          status = 'no_license';
          break;
      }
    }

    return {
      isValid: false,
      hasLicense: status === 'expired', // expired = hatte mal eine
      status,
      daysRemaining: null,
      expiresAt: null,
      expiringSoon: false,
      expiringCritical: false,
      lastChecked: new Date()
    };
  }

  private parseCalStatus(response: RedisMetaResponse): CalStatus {
    const cal = response.cal;

    if (!cal) {
      return {
        isValid: false,
        hasLicense: false,
        status: 'no_license',
        daysRemaining: null,
        expiresAt: null,
        expiringSoon: false,
        expiringCritical: false,
        lastChecked: new Date()
      };
    }

    return {
      isValid: cal.has_license && cal.status === 'active',
      hasLicense: cal.has_license,
      status: cal.status,
      daysRemaining: cal.days_remaining,
      expiresAt: cal.expires_at,
      expiringSoon: cal.expiring_soon,
      expiringCritical: cal.expiring_critical,
      lastChecked: new Date()
    };
  }

  private showCalErrorDialog(cal: RedisMetaResponse['cal']): void {
    if (this.dialogOpen) return;

    // ── v3.0.0: KEIN Dialog wenn Session-Expiry läuft ────────────────
    if (this.sessionHealth.isHandlingExpiry) {
      console.log('[CalStatusService] Suppressing CAL dialog — session expiry in progress');
      return;
    }

    this.dialogOpen = true;

    const dialogData: CalErrorDialogData = {
      status: cal?.status || 'no_license',
      hasLicense: cal?.has_license || false,
      daysRemaining: cal?.days_remaining ?? null,
      expiresAt: cal?.expires_at ?? null,
      assignedBy: cal?.assigned_by ?? null,
      assignedAt: cal?.assigned_at ?? null,
      redirectDelay: 9
    };

    const dialogRef = this.dialog.open(CalErrorDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      disableClose: true,
      data: dialogData,
      panelClass: 'cal-error-dialog-container'
    });

    dialogRef.afterClosed().subscribe(() => {
      this.dialogOpen = false;
    });
  }

  private invalidateCache(): void {
    this.cachedResponse$ = null;
    this.cacheTimestamp = 0;
  }

  private constructApiBaseUrl(): string {
    const hostname = window.location.hostname;
    if (hostname.includes('localhost')) return 'http://localhost:3000';
    return `https://api.${hostname}`;
  }
}
